import { createTheme } from "@mui/material";
import {
  BLACK,
  GREEN_LIGHT,
  GREY,
  GREY_DARK,
  PRIMARY_GREEN,
  TEXT_COLOR_BLACK,
  WHITE,
} from "../utility/constants/common-constants";

// Roboto is the main font if that doen't load for any reason sans-serif will be the fallback font(need to confirm for fallback font)
const fontFamily = ["roboto", "sans-serif"].join(",");

const colors = {
  palette: {
    primary: {
      main: "#a41e35",
    },
    secondary: {
      main: "#403c3c",
    },
    blue: {
      main: "#06A3B0",
    },
    gray: {
      light0: "#F2EEE8",
      main: "#403C3C",
    },
    black: BLACK,
    white: WHITE,
  },
};

const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1560,
  },
};

const companyTheme = createTheme(
  {
    ...colors,
    breakpoints,
    typography: {
      fontFamily,
      h1: {
        fontSize: "25px",
        fontWeight: "700",
        color: BLACK,
      },
      h2: {
        fontSize: "18px",
        fontWeight: "700",
        color: BLACK,
      },
      h3: {
        fontSize: "16px",
        fontWeight: "700",
        color: BLACK,
      },
      h4: {
        fontSize: "14px",
        color: BLACK,
      },
      h5: {
        fontSize: "1.5rem",
        color: BLACK,
      },
      h6: {
        fontSize: "1.25rem",
        color: BLACK,
      },
      subtitle1: {
        fontSize: "14px",
        fontWeight: 400,
        color: BLACK,
      },
      subtitle2: {
        fontSize: "16px",
        fontWeight: 500,
        color: BLACK,
      },
      body1: {
        fontSize: "16px",
        fontWeight: 400,
        color: BLACK,
      },
      body2: {
        fontSize: "14px",
        fontWeight: 400,
        color: BLACK,
      },
    },
  },
  {
    components: {
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            h1: "h1",
            h2: "h2",
            h3: "h3",
            h4: "h4",
            h5: "h5",
            h6: "h6",
            subtitle1: "span",
            subtitle2: "span",
            body1: "p",
            body2: "p",
          },
        },
      },
    },
  }
);

export default companyTheme;

const patienFontFamily = ["proxima-nova", "sans-serif"].join(",");
const patientHeadingFontFamily = ["ivypresto-display", "sans-serif"].join(",");

const patientTheme = createTheme(
  {
    palette: {
      primary: { main: PRIMARY_GREEN, light: GREEN_LIGHT },
      secondary: { light: GREY, main: GREY_DARK },
      common: { black: TEXT_COLOR_BLACK, white: WHITE },
    },
    breakpoints,
    typography: {
      fontFamily: patienFontFamily,
      h1: {
        fontFamily: patientHeadingFontFamily,
        fontSize: "40px",
        fontWeight: "400",
        lineHeight: "48px",
        letterSpacing: "0",
        color: TEXT_COLOR_BLACK,
      },
      h2: {
        fontFamily: patientHeadingFontFamily,
        fontSize: "32px",
        fontWeight: "400",
        lineHeight: "48px",
        letterSpacing: "0",
        color: TEXT_COLOR_BLACK,
      },
      h3: {
        fontFamily: patientHeadingFontFamily,
        fontSize: "28px",
        fontWeight: "400",
        lineHeight: "36px",
        letterSpacing: "0",
        color: TEXT_COLOR_BLACK,
      },
      h4: {
        fontFamily: patientHeadingFontFamily,
        fontSize: "24px",
        fontWeight: "400",
        lineHeight: "32px",
        letterSpacing: "0",
        color: TEXT_COLOR_BLACK,
      },
      h5: {
        fontFamily: patientHeadingFontFamily,
        fontSize: "18px",
        fontWeight: "400",
        lineHeight: "22px",
        letterSpacing: "0",
        color: TEXT_COLOR_BLACK,
      },
      subtitle1: {
        fontFamily: patienFontFamily,
        fontWeight: 500,
        fontSize: "20px",
        lineHeight: "24px",
        letterSpacing: "0",
        color: TEXT_COLOR_BLACK,
      },
      subtitle2: {
        fontFamily: patienFontFamily,
        fontWeight: 600,
        fontSize: "16px",
        lineHeight: "22px",
        letterSpacing: "0",
        color: TEXT_COLOR_BLACK,
      },
      body1: {
        fontFamily: patienFontFamily,
        fontWeight: 400,
        fontSize: "16px",
        lineHeight: "22px",
        letterSpacing: "0",
        color: TEXT_COLOR_BLACK,
      },
      body2: {
        fontFamily: patienFontFamily,
        fontWeight: 400,
        fontSize: "14px",
        lineHeight: "21px",
        letterSpacing: "0",
        color: TEXT_COLOR_BLACK,
      },
      bodySmall: {
        fontFamily: patienFontFamily,
        fontWeight: 400,
        fontSize: "12px",
        lineHeight: "14px",
        letterSpacing: "0",
        color: GREEN_LIGHT,
      },
      bodyOverline: {
        fontFamily: patienFontFamily,
        fontSize: "12px",
        lineHeight: "14px",
        letterSpacing: "10%",
      }
    },
  },
  {
    components: {
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            h1: "h1",
            h2: "h2",
            h3: "h3",
            h4: "h4",
            h5: "h5",
            h6: "h6",
            subtitle1: "span",
            subtitle2: "span",
            body1: "p",
            body2: "p",
            bodySmall: "span",
          },
        },
      },
    },
  }
);

// Extend MUI Typography to support custom variants
declare module "@mui/material/styles" {
  interface TypographyVariants {
    bodySmall: React.CSSProperties;
    bodyOverline: React.CSSProperties;
  }

  // Allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    bodySmall?: React.CSSProperties;
    bodyOverline?: React.CSSProperties;
  }
}
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    bodySmall: true;
    bodyOverline: true;
  }
}

export { patientTheme };
